import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Card,
  Icon,
  Chip,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  DialogContent,
  Dialog,
  DialogTitle,
  DialogActions,
  Divider,
} from "@mui/material";
import axios from "axios.js";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

// Layout components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import DataTable from "theme/Tables/DataTable";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

function QAReviewsLogPage() {
  const pageTitle = "Voice - Call Center";
  usePageTitle(pageTitle);
  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [review, setReview] = useState(null);

  const columns = [
    { Header: "Reference", accessor: "reference", align: "left" },
    { Header: "Reviewed On", accessor: "created", align: "left" },
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Auditor (QA)", accessor: "user", align: "left" },
    { Header: "Template Used", accessor: "template", align: "left" },
    { Header: "Final Score", accessor: "score", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  function buildRows(data) {
    const formattedRows = data.map((row) => ({
      reference: (
        <MDTypography variant="caption" fontWeight="medium">
          #{row.reference.substring(0, 8)}
        </MDTypography>
      ),
      created: row.created,
      agent: row.agent,
      user: row.user,
      template: row.template,
      score: (
        <Chip
          label={`${row.score}%`}
          size="small"
          sx={{
            backgroundColor:
              row.score >= 70 ? "success.main" : row.score >= 50 ? "warning.main" : "error.main",
            color: "#fff",
            fontWeight: "bold",
          }}
        />
      ),
      action: (
        <MDButton
          variant="outlined"
          color="dark"
          size="small"
          iconOnly
          title="View Full"
          onClick={() => getReview(row.reference)}
        >
          <Icon>visibility</Icon>
        </MDButton>
      ),
    }));

    return formattedRows;
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/voice/call_centers/qa/reviews/${ref}/all.json`)
        .then((result) => {
          console.log(result.data.data);
          setShowLoading(false);
          if (result.data.status === "success") {
            // setCurrency(result.data.currency);
            const rows = buildRows(result.data.data.reviews);
            setReviews(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function getReview(reviewRef) {
    setShowLoading(true);
    await axios
      .get(`portal/voice/call_centers/qa/reviews/${reviewRef}/fetch.json`)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setReview(result.data.data.review);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {review && (
        <Dialog open fullWidth maxWidth="sm">
          <DialogTitle>Review Breakdown</DialogTitle>
          <DialogContent>
            <MDBox mt={1}>
              {/* Call Details Sub-Pane */}
              <Card sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa", boxShadow: "none" }}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color="text"
                  display="block"
                  mb={1}
                >
                  CALL DETAILS
                </MDTypography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Agent: <b>{review.agent}</b>
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Duration: <b>{review.duration}s</b>
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text">
                      Call Time: <b>{review.call_date}</b>
                    </MDTypography>
                  </Grid>
                </Grid>

                {/* Audio Asset Core Loader */}
                {review.recording ? (
                  <MDBox mt={2}>
                    <audio
                      src={`${AppConfig.api_media_url}${review.recording}`}
                      controls
                      style={{ width: "100%" }}
                    />
                  </MDBox>
                ) : (
                  <MDTypography variant="caption" color="error" display="block" mt={2}>
                    ⚠️ Call recording file unavailable
                  </MDTypography>
                )}
              </Card>

              <Divider />

              {/* Individual Metrics Iteration Block */}
              <MDTypography variant="button" fontWeight="bold" color="text" display="block" my={2}>
                FINAL SCORE ({review.score}%)
              </MDTypography>

              {review.metrics.map((metric, idx) => (
                <MDBox
                  key={metric.reference}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={1}
                  sx={{ borderBottom: "1px dashed #e0e0e0" }}
                >
                  <MDTypography variant="body2">
                    {idx + 1}. {metric.title}
                  </MDTypography>
                  <Chip
                    label={`${metric.score}`}
                    size="small"
                    variant="outlined"
                    color={metric.score >= 70 ? "success" : "error"}
                  />
                </MDBox>
              ))}

              {review.notes && (
                <MDBox mt={3}>
                  <MDTypography variant="button" fontWeight="bold" color="text" display="block">
                    AUDITOR REMARKS
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      fontStyle: "italic",
                      mt: 0.5,
                      p: 1.5,
                      bgcolor: "#fafafa",
                      borderRadius: "4px",
                    }}
                  >
                    {review.notes}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setReview(null)} color="dark" startIcon={<Icon>close</Icon>}>
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
      {pageLoaded && (
        <MDBox mt={1}>
          <MDBox mt={0} mb={2} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/voice/call-centers/${ref}`);
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox>
              <Button
                size="small"
                color="info"
                iconOnly
                onClick={() => {
                  navigate(`/voice/call-centers/quality-control/reports/${ref}`);
                }}
                startIcon={
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    leaderboard
                  </Icon>
                }
              >
                Insights
              </Button>
            </MDBox>
          </MDBox>
          <Card>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  Quality Assurance Logs
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox>
              <DataTable
                table={{ columns, rows: reviews }}
                isSorted={false}
                entriesPerPage={true}
                showTotalEntries={true}
                noEndBorder
              />
            </MDBox>
          </Card>
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default QAReviewsLogPage;
